import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Button, Field, Input } from '../components/ui';
import { AuthShell } from '../components/AuthShell';

/**
 * Signing in.
 *
 * This screen used to open with the demo owner's address and `password123`
 * already typed in, and a line underneath naming every seeded account. That
 * was right while the only people who could reach it were the person building
 * it — and became wrong the moment anybody could sign up here, because the
 * deployed app was then publishing working credentials for its own demo
 * company to the open internet, from a repository that is also public.
 *
 * Empty fields now. A developer who wants the seeded accounts can read the
 * seed file, which is where that belongs.
 */
export function LoginPage() {
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signIn(email.trim(), password);
    } catch (e) {
      setError((e as { message?: string }).message ?? 'Could not sign in');
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell title="Sign in">
      <form onSubmit={onSubmit} className="space-y-4">
        <Field label="Email">
          <Input
            type="email"
            value={email}
            autoComplete="username"
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </Field>
        <Field label="Password">
          <Input
            type="password"
            value={password}
            autoComplete="current-password"
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </Field>

        {error && (
          <p className="rounded-lg bg-danger-soft px-3 py-2 text-[13px] text-danger">{error}</p>
        )}

        <Button type="submit" variant="primary" size="lg" className="w-full" loading={busy}>
          Sign in
        </Button>
      </form>

      {/*
        Under the button rather than beside the password field. Somebody who
        knows their password should never have to read past it, and somebody
        who does not will look for it after the attempt that failed.
      */}
      <div className="mt-5 space-y-2 text-center text-[13px] text-ink-3">
        <p>
          <Link to="/forgot-password" className="font-semibold text-accent">
            Forgot your password?
          </Link>
        </p>
        <p>
          New here?{' '}
          <Link to="/signup" className="font-semibold text-accent">
            Start your company
          </Link>
        </p>
      </div>
    </AuthShell>
  );
}
